import type { ReactNode } from 'react';

import { isEmojiOnlyText } from '../_lib/whatsapp-emoji';
import { ReadCheck } from './whatsapp-read-check';

/**
 * Die eine Chat-Bubble des WhatsApp-Mocks. Ersetzt die zuvor fünf parallelen
 * Bubble-Renderer (Marketing-Seiten, Home, Concierge-LP, Onboarding-Explainer)
 * und ist damit der einzige Ort, an dem die Emoji-only-Regel, der Read-Haken und
 * die Klassenreihenfolge festgelegt sind.
 *
 * Bewusst OHNE `'use client'` und ausserhalb von `_components/ui` (= Design-Sync
 * `srcDir`): die Komponente ist rein darstellend, soll nicht in den
 * Client-Bundle-Graph gezogen werden und keine Preview-Card auslösen (Spec §9.1).
 *
 * Heisst `WaMessageBubble` und NICHT `WaBubble` — letzteres ist das Daten-Interface
 * in `so-funktionierts/_content.tsx` (Namenskollision, Spec §5.1).
 */
export interface WaMessageBubbleProps {
  /** `data-wa-step` (steps-Engine) bzw. `data-wac-step` (reveal-Engine). */
  step?: string;
  stepAttribute?: 'wa' | 'wac';
  dir: 'in' | 'out';
  text: ReactNode;
  time: string;
  /** Read-Haken. Default: `dir === 'out'`. */
  readCheck?: boolean;
  /** Zusatzklassen: `has-qr`, `has-status`, `whatsapp-bubble--id`, `whatsapp-bubble--reply`. */
  variantClass?: string;
  /** Statisch sichtbar (explainer/reveal-Erstframe) statt Engine-gesteuert. */
  visible?: boolean;
  /** Slot zwischen Text und Zeit (Quick-Replies, Status-Chip). */
  children?: ReactNode;
}

export function WaMessageBubble({
  step,
  stepAttribute = 'wa',
  dir,
  text,
  time,
  readCheck,
  variantClass,
  visible,
  children,
}: WaMessageBubbleProps) {
  const showCheck = readCheck ?? dir === 'out';
  // `children != null` (Quick-Replies, Status-Chip) schaltet den Emoji-Zweig hart
  // ab: eine Bubble mit Zusatzinhalt ist konstruktiv keine reine Emoji-Nachricht.
  const emojiOnly = children == null && isEmojiOnlyText(text);

  // Reihenfolge ist gegen die Bestands-Renderer verifiziert: `is-visible` steht
  // VOR der Variantenklasse (`whatsapp-bubble--id`/`--reply`).
  const className = [
    'whatsapp-bubble',
    `whatsapp-bubble--${dir}`,
    emojiOnly ? 'whatsapp-bubble--emoji' : '',
    visible ? 'is-visible' : '',
    variantClass ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const stepProps =
    step === undefined
      ? {}
      : stepAttribute === 'wac'
        ? { 'data-wac-step': step }
        : { 'data-wa-step': step };

  return (
    <div className={className} {...stepProps}>
      <span className="whatsapp-bubble-text">{text}</span>
      {children}
      <span className="whatsapp-bubble-time">
        {time}
        {showCheck ? <ReadCheck /> : null}
      </span>
    </div>
  );
}
